import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const DAYS = [
  { key: "sunday", label: "Sunday" }, { key: "monday", label: "Monday" }, { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" }, { key: "thursday", label: "Thursday" }, { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
];
const EMPTY = { sunday: 0, monday: 1, tuesday: 1, wednesday: 1, thursday: 1, friday: 1, saturday: 0 };

export function StartDaysCard() {
  const [days, setDays] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/outlet/start-days")
      .then(({ data }) => setDays(data))
      .catch((error) => toast.error(getApiErrorMessage(error, "Could not load start-date settings.")))
      .finally(() => setLoading(false));
  }, []);

  const toggleDay = (key) => setDays((d) => ({ ...d, [key]: d[key] ? 0 : 1 }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/outlet/start-days", days);
      setDays(data);
      toast.success("New-subscription start days updated.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not update start-date settings."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="settings-start-days-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <CalendarPlus size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">New Subscription Start Days</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Days a customer is allowed to pick as the start date of a NEW subscription. This does not affect
              deliveries — Sunday deliveries and existing subscriptions keep running. You can still manually set
              any start date (including Sat/Sun) from a customer's subscription profile.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          {DAYS.map((d) => (
            <label key={d.key} className="flex items-center gap-2 text-sm text-text-main" data-testid={`settings-start-day-${d.key}`}>
              <Checkbox checked={Boolean(days[d.key])} disabled={loading} onCheckedChange={() => toggleDay(d.key)} />
              {d.label}
            </label>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={handleSave} disabled={saving || loading} data-testid="settings-start-days-save-button">
            {saving ? "Saving..." : "Save Start Days"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
